/* eslint-disable arrow-body-style */
/* eslint-disable react/prop-types */
import React, { useState, useCallback } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';

import { GLOBAL_DATE_FORMAT, TABLE_HEIGHT } from 'constant';

import CTable from 'components/CTable';
import { allyProps } from 'views/Reports/TreatmentEvalReport/utils';
import PatientLink from 'components/common/PatientLink';
import { useGetAssessmentListQuery, useUnlockAssessmentMutation } from 'store/services/pshychologicalAssessment';
import useAuth from 'hooks/useAuth';
import { Stack } from '@mui/system';
import { Link } from 'react-router-dom';
import { Lock, LockOpen, VisibilityTwoTone } from '@mui/icons-material';
import { encriptId } from 'utils/encription';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import moment from 'moment';
import AssessmentView from './AssessmentView';
import { useAssessmentForm } from './useAssessmentForm';
import Spinner from 'ui-component/Spinner';
import ConfirmationModal, { useConfirmation } from 'components/reusable/ConfirmationModal';
import useSnackbar from 'hooks/useSnackbar';
import usePermission from 'hooks/usePermission';
import HistoryViewModal from './HistoryView';
import HistoryIcon from '@mui/icons-material/History';

export const cols = (
    hasPermission,
    handleOpenDelete,
    handleUnlockOpen,
    parent,
    handleView,
    isSuperAdmin,
    isSameAsMasterAdmin,
    isMaster,
    empId,
    handleHistory
) => [
    {
        field: 'date',
        headerName: 'Date',
        valueGetter: (value, row) => moment.utc(row?.date).local().format(GLOBAL_DATE_FORMAT),
        ...allyProps,
        maxWidth: 100
    },
    {
        field: 'patient',
        headerName: 'Patient',
        align: 'center',
        headerAlign: 'center',
        ...allyProps,
        renderCell: (params) => <PatientLink name={params?.row?.patient?.name} id={params?.row?.patient?.id} />
    },
    {
        field: 'location',
        headerName: 'Location',
        align: 'center',
        headerAlign: 'center',
        ...allyProps,
        valueGetter: (value, row) => row?.location?.name
    },
    {
        field: 'employee',
        headerName: 'Employee',
        align: 'center',
        headerAlign: 'center',
        ...allyProps,
        valueGetter: (value, row) => {
            const v = row?.employee;
            return v ? `${v.name}${v.credentials ? `, ${v.credentials}` : ''}` : null;
        }
    },

    {
        field: 'actions',
        headerName: 'Actions',
        align: 'center',
        headerAlign: 'center',
        ...allyProps,
        renderCell: (params) => {
            return (
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                        justifyContent: "center",
                        alignItems: "center"
                    }}>
                    {hasPermission(['psychological_testing_view']) && (
                        <Tooltip title="View">
                            <IconButton color="primary" size="large" onClick={() => handleView(params?.row.id)}>
                                <VisibilityTwoTone color="secondary" sx={{ fontSize: '1.3rem' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {parent === 'in_progress' &&
                        hasPermission(['psychological_testing_edit']) &&
                        // Condition 1: Super admins or masters always see the button
                        (isSuperAdmin ||
                            isSameAsMasterAdmin ||
                            isMaster ||
                            // Condition 2: Otherwise check if employee matches
                            params.row?.employee?.id === empId) && (
                            <Tooltip title="Edit">
                                <Link to={`edit/${encriptId(params.row.id)}`}>
                                    <IconButton title="Edit" color="secondary">
                                        <EditIcon />
                                    </IconButton>
                                </Link>
                            </Tooltip>
                        )}
                    {hasPermission(['psychological_testing_view']) && (
                        <Tooltip title="View History">
                            <IconButton color="secondary" size="large" onClick={() => handleHistory(params?.row.id)}>
                                <HistoryIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {parent === 'in_progress' &&
                        hasPermission(['psychological_testing_edit']) &&
                        // Condition 1: Super admins or masters always see the button
                        (isSuperAdmin ||
                            isSameAsMasterAdmin ||
                            isMaster ||
                            // Condition 2: Otherwise check if employee matches
                            params.row?.employee?.id === empId) && (
                            <IconButton color="secondary" onClick={() => handleOpenDelete(params.row.id)}>
                                <DeleteIcon color="primary" />
                            </IconButton>
                        )}
                    {parent === 'completed' &&
                        hasPermission(['psychological_testing_unlock']) &&
                        (isSuperAdmin || isSameAsMasterAdmin || isMaster) &&
                        !params.row?.is_batched && (
                            <IconButton title="Unlock" color="primary" onClick={() => handleUnlockOpen(params.row.id)}>
                                <LockOpen />
                            </IconButton>
                        )}
                    {params?.row?.is_full_signed && (
                        <IconButton title="Fully Signed" color="info">
                            <Lock />
                        </IconButton>
                    )}
                </Stack>
            );
        }
    }
];

const Table = ({ parent, filterValues, patient, setDisableFilter, page, setPage }) => {
    const { user } = useAuth();
    const [assessmentId, setAssessmentId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);

    const { notify, notifyError } = useSnackbar();
    // console.log('filterValuesLL', filterValues);
    const { handleClickOpen } = useConfirmation();
    const { hasPermission, isMaster, isSameAsMasterAdmin, isSuperAdmin } = usePermission();
    const payload = {
        filters: {
            client_id: user?.client_id,
            is_full_signed: parent === 'completed',
            location_ids: filterValues.locations,
            patient_ids: patient?.id && [patient?.id],
            ...(filterValues.startDate && {
                start_date: moment(new Date(filterValues.startDate)).startOf('day').utc().format('YYYY-MM-DD HH:mm')
            }),
            ...(filterValues.endDate && {
                end_date: moment(new Date(filterValues.endDate)).endOf('day').utc().format('YYYY-MM-DD HH:mm')
            }),
            employee_ids: filterValues?.therapists
        },
        pagination: { current_page: parseInt(page, 10) }
    };
    const { data: listData, isFetching, isLoading } = useGetAssessmentListQuery(payload);
    const { detailFetching, detailLoading, detail, deleteAssessment, isDeleting } = useAssessmentForm({
        AssessmentId: assessmentId
    });

    const handlePageChange = (_e, val) => setPage(val);

    const handleView = (id) => {
        setDisableFilter(true);
        setAssessmentId(id);
    };

    const handleClose = (id) => {
        setDisableFilter(false);
        setAssessmentId(null);
    };
    const handleCloseDelete = () => {
        setDeleteId(null);
    };
    const handleOpenDelete = async (id) => {
        // Add your delete logic here
        setDeleteId(id);
    };

    const handleDelete = useCallback(async (id) => {
        try {
            const res = await deleteAssessment({ id }).unwrap();
            notify({ message: res?.message?.[0], severity: 'success' });
            setDeleteId(null);
        } catch (error) {
            notify({ message: error?.data?.message[0], severity: 'warning' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [unlock, { isLoading: unlockLoading }] = useUnlockAssessmentMutation();

    const handleUnlockClick = (id) => {
        const unlockAssessment = async () => {
            const payload = {
                id
            };
            try {
                const res = await unlock(payload).unwrap();
                console.log({ res });
                notify({
                    message: res.message,
                    severity: 'success'
                });
            } catch (error) {
                notifyError({ error });
            }
        };

        handleClickOpen({
            title: 'Unlock Psychological Testing',
            content: `Are you sure you want to unlock this Psychological Testing ?`,
            onConfirm: () => unlockAssessment
        });
    };

    const [historyId, setHistoryId] = useState(null);

    const handleHistory = (id) => {
        setHistoryId(id);
    };

    const handleHistoryClose = () => {
        setHistoryId(null);
    };

    return (
        <Box sx={{ height: TABLE_HEIGHT.LARGE, my: 1 }}>
            {detailFetching || (detailLoading && <Spinner />)}
            <CTable
                loading={isFetching || isLoading}
                rows={listData?.fields || []}
                cols={cols(
                    hasPermission,
                    handleOpenDelete,
                    handleUnlockClick,
                    parent,
                    handleView,
                    isSuperAdmin,
                    isSameAsMasterAdmin,
                    isMaster,
                    user?.emp_id,
                    handleHistory
                )} // Pass all 5 parameters
                getRowId={(option) => option.id}
                currentPage={page}
                totalCount={listData?.pagination?.total_records}
                countPerPage={listData?.pagination?.records_per_page}
                onPageChange={handlePageChange}
            />
            {assessmentId && <AssessmentView open={assessmentId} data={detail?.fields} onClose={handleClose} />}
            <ConfirmationModal
                open={deleteId}
                handleClose={handleCloseDelete}
                handleConfirm={() => {
                    handleDelete(deleteId);
                }}
                title="Delete Psychological Testing"
                description="Are you sure you want to delete this assessment ?"
            />
            <HistoryViewModal historyId={historyId} handleClose={handleHistoryClose} />
        </Box>
    );
};

export default Table;
